/**
 * TypeScript types for the This-or-That Preferences Game
 *
 * The game presents users with binary choices (either images or text)
 * to help determine their home preferences in an engaging way.
 *
 * These types match the API response shapes from:
 * - POST /api/account/preferences-game (start game session)
 * - GET /api/account/preferences-game (get current session)
 * - POST /api/account/preferences-game/respond (submit choice)
 * - POST /api/account/preferences-game/complete (finalize preferences)
 * - DELETE /api/account/preferences-game (abandon session)
 */

// ============================================================================
// Core Game Types
// ============================================================================

/**
 * The type of choice presented in a round
 * - 'image': Two images are shown side by side
 * - 'text': Two text phrases/concepts are shown
 */
export type GameRoundType = 'image' | 'text';

/**
 * Category of preference being explored
 * These map to different aspects of home preferences
 */
export type PreferenceCategory =
  | 'layout'           // Open vs closed floor plans, room arrangements
  | 'style'            // Modern vs traditional, minimalist vs ornate
  | 'outdoor'          // Yard size, landscaping, outdoor living
  | 'kitchen'          // Kitchen style, appliances, layout
  | 'lighting'         // Natural light, window types
  | 'neighborhood'     // Urban vs suburban, walkability
  | 'storage'          // Closet space, garage, basement
  | 'finishes'         // Flooring, countertops, fixtures
  | 'size'             // Cozy vs spacious, room count
  | 'features';        // Pool, fireplace, smart home

/**
 * A single option in a this-or-that choice
 */
export interface GameOption {
  /** Unique identifier for this option */
  id: string;
  /** Display label for the option */
  label: string;
  /** Optional description providing more context */
  description?: string;
  /** For image rounds: URL to the image */
  imageUrl?: string;
  /** For image rounds: Alt text for accessibility */
  imageAlt?: string;
  /** Internal preference value this option represents */
  preferenceValue: string;
}

/**
 * A single round in the game presenting two options
 */
export interface GameRound {
  /** Unique identifier for this round */
  id: string;
  /** The type of choice (image or text) */
  type: GameRoundType;
  /** Category this round explores */
  category: PreferenceCategory;
  /** Question or prompt for the user */
  prompt: string;
  /** The two options to choose from */
  optionA: GameOption;
  optionB: GameOption;
  /** Order this round appears in the session (1-indexed) */
  roundNumber: number;
}

/**
 * User's response to a game round
 */
export interface GameResponse {
  /** The round ID being responded to */
  roundId: string;
  /** Which option was chosen: 'A' or 'B' */
  choice: 'A' | 'B';
  /** Timestamp when the choice was made */
  chosenAt: string;
  /** Optional: how confident the user felt (future feature) */
  confidence?: number;
}

/**
 * A game session tracking the user's progress
 */
export interface GameSession {
  /** Unique session identifier */
  id: number;
  /** UUID for external reference */
  uuid: string;
  /** User ID who owns this session */
  userId: number;
  /** Current round number (0 = not started) */
  currentRound: number;
  /** Total number of rounds in this session */
  totalRounds: number;
  /** Whether the session is complete */
  isComplete: boolean;
  /** When the session was started */
  startedAt: string;
  /** When the session was completed (if complete) */
  completedAt?: string;
  /** Final synthesized preferences (if complete) */
  finalPreferences?: string;
}

// ============================================================================
// API Request Types
// ============================================================================

/**
 * Request body for starting a new game session
 */
export interface StartGameRequest {
  /** Number of rounds (default: 10) */
  totalRounds?: number;
  /** Mix of image vs text rounds (default: 'mixed') */
  roundTypePreference?: 'image' | 'text' | 'mixed';
  /** Categories to focus on (default: all) */
  categories?: PreferenceCategory[];
}

/**
 * Request body for submitting a round response
 */
export interface GameRespondRequest {
  /** The session ID */
  sessionId?: number;
  /** The round ID being responded to */
  roundId: string;
  /** The chosen option: 'A' or 'B' */
  choice: 'A' | 'B';
}

/**
 * Request body for completing the game
 */
export interface GameCompleteRequest {
  /** The session ID to complete */
  sessionId: number;
}

/**
 * Request body for abandoning the game
 */
export interface GameAbandonRequest {
  /** The session ID to abandon */
  sessionId: number;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from POST /api/account/preferences-game (start game)
 */
export interface StartGameResponse {
  success: boolean;
  sessionId?: number;
  sessionUuid?: string;
  /** First round to display */
  firstRound?: GameRound;
  totalRounds?: number;
  error?: string;
}

/**
 * Response from GET /api/account/preferences-game (get current session)
 */
export interface GetGameResponse {
  success: boolean;
  /** Whether an active game session exists */
  isActive: boolean;
  sessionId?: number;
  sessionUuid?: string;
  /** Current round to display */
  currentRound?: GameRound;
  /** Current round number (1-indexed) */
  roundNumber?: number;
  totalRounds?: number;
  /** Previous responses in this session */
  responses?: GameResponse[];
  isComplete?: boolean;
  error?: string;
}

/**
 * Response from POST /api/account/preferences-game/respond
 */
export interface GameRespondResponse {
  success: boolean;
  /** Next round to display (null if game complete) */
  nextRound?: GameRound | null;
  /** Updated round number */
  roundNumber?: number;
  totalRounds?: number;
  /** Whether the game is now complete */
  isComplete?: boolean;
  error?: string;
}

/**
 * Response from POST /api/account/preferences-game/complete
 */
export interface GameCompleteResponse {
  success: boolean;
  /** Synthesized preferences from all responses */
  preferences?: string;
  /** Session ID for reference */
  sessionId?: number;
  /** Summary of choices made */
  summary?: {
    totalRounds: number;
    categoriesCovered: PreferenceCategory[];
    strongPreferences: string[];
  };
  error?: string;
}

/**
 * Response from DELETE /api/account/preferences-game
 */
export interface GameAbandonResponse {
  success: boolean;
  error?: string;
}

/**
 * Response from GET /api/account/preferences-game/round
 * Returns the current round with options for the active session
 */
export interface GetRoundResponse {
  success: boolean;
  /** The current round to display */
  round?: GameRound;
  /** Current round number (1-indexed) */
  roundNumber?: number;
  /** Total rounds in the session */
  totalRounds?: number;
  /** Session ID for reference */
  sessionId?: number;
  /** Whether the game session is complete */
  isComplete?: boolean;
  error?: string;
}

// ============================================================================
// Frontend State Types
// ============================================================================

/**
 * Complete game state for the frontend
 * Aggregates session info for UI rendering
 */
export interface GameState {
  /** Whether a game session is currently active */
  isActive: boolean;
  /** The game session ID */
  sessionId: number | null;
  /** The session UUID for reference */
  sessionUuid: string | null;
  /** Current round being displayed */
  currentRound: GameRound | null;
  /** Current round number (1-indexed) */
  roundNumber: number;
  /** Total rounds in the session */
  totalRounds: number;
  /** Whether the game has been completed */
  isComplete: boolean;
  /** Previous responses (for review/back navigation) */
  responses: GameResponse[];
  /** Final preferences if game is complete */
  finalPreferences?: string;
}

/**
 * Callback for preloading next round (used by UI to preload images)
 */
export type NextRoundPreloadCallback = (nextRound: GameRound | null) => void;

/**
 * Hook return type for usePreferencesGame
 */
export interface UsePreferencesGameReturn {
  /** Current game state */
  state: GameState;
  /** Whether an API request is in progress */
  isLoading: boolean;
  /** Latest error message, if any */
  error: string | null;
  /** Start a new game session */
  startGame: (options?: StartGameRequest) => Promise<boolean>;
  /** Submit a choice for the current round */
  submitChoice: (choice: 'A' | 'B') => Promise<boolean>;
  /** Complete the game and get synthesized preferences */
  completeGame: (updatePreferences?: boolean) => Promise<string | null>;
  /** Abandon the current game session */
  abandonGame: () => Promise<boolean>;
  /** Refresh the current game state from the server. Returns true if an active session was found. */
  refreshState: () => Promise<boolean>;
  /** Clear any error state */
  clearError: () => void;
  /** Register a callback to preload the next round as soon as it's received from API */
  setNextRoundPreloadCallback: (callback: NextRoundPreloadCallback | null) => void;
}

/**
 * Default/initial game state
 */
export const INITIAL_GAME_STATE: GameState = {
  isActive: false,
  sessionId: null,
  sessionUuid: null,
  currentRound: null,
  roundNumber: 0,
  totalRounds: 10,
  isComplete: false,
  responses: [],
  finalPreferences: undefined,
};

// ============================================================================
// Database Row Types (for mapping)
// ============================================================================

/**
 * Raw database row from preference_game_sessions table
 */
export interface GameSessionRow {
  id: number;
  uuid: string;
  user_id: number;
  current_round: number;
  total_rounds: number;
  is_complete: number; // SQLite boolean stored as 0/1
  started_at: string;
  completed_at: string | null;
  final_preferences: string | null;
}

/**
 * Raw database row from preference_game_rounds table
 */
export interface GameRoundRow {
  id: string;
  session_id: number;
  round_number: number;
  type: string;
  category: string;
  prompt: string;
  option_a_id: string;
  option_a_label: string;
  option_a_description: string | null;
  option_a_image_url: string | null;
  option_a_image_alt: string | null;
  option_a_preference_value: string;
  option_b_id: string;
  option_b_label: string;
  option_b_description: string | null;
  option_b_image_url: string | null;
  option_b_image_alt: string | null;
  option_b_preference_value: string;
  user_choice: string | null;
  chosen_at: string | null;
}

// ============================================================================
// NOTE: Mapping functions (mapGameSessionRow, mapGameRoundRow)
// are defined in shared/preferences-game/GameRepository.ts for backend use.
// This file contains only type definitions for frontend/shared use.
// ============================================================================

// ============================================================================
// Integration Types (for connecting game with preferences system)
// ============================================================================

/**
 * Structured insight extracted from a game response
 */
export interface GameInsight {
  /** The preference category this insight relates to */
  category: PreferenceCategory;
  /** What the user preferred */
  preferred: string;
  /** What the user did not prefer */
  notPreferred: string;
  /** The original prompt/question */
  prompt: string;
  /** Confidence level based on response consistency (0-1) */
  confidence: number;
}

/**
 * Aggregated insights from a completed game session
 */
export interface GameInsightsResult {
  /** Session ID the insights came from */
  sessionId: number;
  /** When the game was completed */
  completedAt: string;
  /** Individual insights from each round */
  insights: GameInsight[];
  /** Categories covered in the game */
  categoriesCovered: PreferenceCategory[];
  /** Summary text generated by the game (if available) */
  synthesizedPreferences: string | null;
  /** Number of rounds completed */
  totalRounds: number;
}

/**
 * Response from GET /api/account/preferences-game/insights
 */
export interface GetInsightsResponse {
  success: boolean;
  /** Single insights result, array of results, or formatted text */
  insights?: GameInsightsResult | GameInsightsResult[] | string;
  /** Whether user has any completed games */
  hasInsights?: boolean;
  error?: string;
}

/**
 * Options for merging game preferences with existing preferences
 */
export interface MergePreferencesOptions {
  /** How to combine with existing preferences */
  mergeStrategy: 'append' | 'prepend' | 'replace';
  /** Whether to include a header indicating source */
  includeSourceHeader: boolean;
  /** Custom header text */
  headerText?: string;
}

/**
 * Extended hook return type with integration helpers
 */
export interface UsePreferencesGameWithIntegrationReturn extends UsePreferencesGameReturn {
  /** Fetch insights from completed games */
  getInsights: (sessionId?: number) => Promise<GameInsightsResult[] | null>;
  /** Check if user has completed game insights */
  hasInsights: () => Promise<boolean>;
  /** Get formatted insights text for display */
  getFormattedInsights: (sessionId?: number) => Promise<string | null>;
}
